import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "./auth-context";
import { usePreviewAs } from "./preview-as-context";
import type { UserRole } from "@colab/shared";

type Props = {
  role: UserRole;
  /**
   * When true, internal users may enter this section IF they've activated
   * preview-as via the partner UUID handshake on the worker. Used by the
   * /portal/* tree so admins can preview the partner experience without
   * impersonation.
   */
  allowInternalPreviewAs?: boolean;
};

// Replaces the request-time role gate from /middleware.ts. Each section layout
// is wrapped by one of these. Renders nothing until auth state has resolved
// (avoids a flash of mismatched UI on initial load).
export function ProtectedRoute({ role, allowInternalPreviewAs = false }: Props) {
  const { session, loading } = useAuth();
  const { preview } = usePreviewAs();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (session.role === role) {
    if (role === "partner" && !session.partnerId) {
      return <Navigate to="/portal/onboarding-pending" replace />;
    }
    return <Outlet />;
  }

  // Cross-role: internal user previewing the partner experience is allowed.
  if (session.role === "internal" && role === "partner" && allowInternalPreviewAs && preview) {
    return <Outlet />;
  }

  // Otherwise, bounce to the section that matches the user's role.
  const home = session.role === "internal" ? "/admin/dashboard" : "/portal/dashboard";
  return <Navigate to={home} replace />;
}
